import React, { useCallback } from 'react'
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone'
import block from 'bem-cn'

import './DropzoneWrapper.scss'

const b = block('dropzone-wrapper')

type Options = DropzoneOptions & {
  maxWidth?: number
  maxHeight?: number
  maxLengthFileName?: number
}

interface DropzoneWrapperProps {
  setTemporaryImage: (file: File) => void
  callbackForLargeFile?: () => void
  callbackForUnsupportedTypeFile?: () => void
  callbackForUnsupportedResolutionFile?: () => void
  callbackLargeLengthFileName?: () => void
  options: Options
  className?: string
}

const DropzoneWrapper: React.FC<DropzoneWrapperProps> = props => {
  const {
    setTemporaryImage,
    callbackForLargeFile,
    callbackForUnsupportedTypeFile,
    callbackForUnsupportedResolutionFile,
    callbackLargeLengthFileName,
    children,
    options,
    className,
  } = props

  const onDrop: DropzoneOptions['onDrop'] = useCallback(
    (acceptedFiles, fileRejections: FileRejection[]) => {
      fileRejections.forEach(file => {
        file.errors.forEach(err => {
          if (err.code === 'file-too-large') {
            callbackForLargeFile?.()
          }
          if (err.code === 'file-invalid-type') {
            callbackForUnsupportedTypeFile?.()
          }
        })
      })

      if (acceptedFiles[0] && options.maxLengthFileName && callbackLargeLengthFileName) {
        if (acceptedFiles[0].name.length > options.maxLengthFileName) {
          callbackLargeLengthFileName()
          return
        }
      }

      if (acceptedFiles.length) {
        const img = new Image()
        const src = URL.createObjectURL(acceptedFiles[0])
        img.src = src

        img.onload = () => {
          const { naturalWidth, naturalHeight } = img

          if (
            options.maxHeight &&
            options.maxWidth &&
            (options.maxHeight < naturalHeight || options.maxWidth < naturalWidth)
          ) {
            callbackForUnsupportedResolutionFile?.()
            return
          }

          setTemporaryImage(acceptedFiles[0])

          URL.revokeObjectURL(src)
        }
      }
    },
    [
      callbackForLargeFile,
      callbackForUnsupportedResolutionFile,
      callbackForUnsupportedTypeFile,
      callbackLargeLengthFileName,
      options,
      setTemporaryImage,
    ],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...options,
    multiple: options.multiple ?? false,
    maxFiles: options.maxFiles ?? 1,
    onDrop,
  })

  return (
    <div {...getRootProps({ className: b({ isDragActive }).mix(className) })}>
      <input {...getInputProps({ hidden: true })} />
      {children}
    </div>
  )
}

export { DropzoneWrapper }
